import { getDefaultStore } from 'jotai';
import { authUserMetaAtom, type AuthUserMeta } from '@/store/authAtoms';
import { selectedFacilityAtom, type Facility } from '@/store/facilityAtom';
import { siteGroupExperienceAtom } from '@/store/siteGroupExperienceAtom';

/**
 * D13 — Walmart Self-Reg interrupt (`/Schedule/?SiteGroup=B` while on SiteGroup A).
 * Prior SiteGroup is pinned write-once in sessionStorage before branding flips to B.
 * Any exit from `/self-reg` (confirm X, wizard close, finish, …) must restore A.
 */
export const SELF_REG_PRIOR_CONTEXT_KEY = 'pp_self_reg_prior_context';
export const SELF_REG_PRIOR_FACILITY_KEY = 'pp_self_reg_prior_facility';
export const SELF_REG_SITE_GROUP_ENTRY_KEY = 'pp_self_reg_site_group_entry';
export const FACILITY_STORAGE_KEY = 'selected_facility';
export const EXPERIENCE_STORAGE_KEY = 'site_group_experience';

export interface SelfRegPriorContext {
  facility: Facility | null;
  authMeta: AuthUserMeta | null;
  returnPath: string;
}

function isFullSession(meta: AuthUserMeta | null | undefined): boolean {
  if (!meta) return false;
  return meta.session?.type !== 'guest' && meta.session?.type !== 'icode';
}

function parseFacility(raw: string | null): Facility | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Facility | null;
    if (!parsed?.id) return null;
    return { id: parsed.id, name: parsed.name || parsed.id };
  } catch {
    return null;
  }
}

export function readPersistedFacility(): Facility | null {
  try {
    return parseFacility(localStorage.getItem(FACILITY_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** SiteGroup from cached experience — often still A while selected_facility was already flipped to B. */
function readPersistedExperienceSiteGroup(): Facility | null {
  try {
    const raw = localStorage.getItem(EXPERIENCE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { siteGroupCode?: string } | null;
    const code = parsed?.siteGroupCode?.trim();
    if (!code) return null;
    return { id: code, name: code };
  } catch {
    return null;
  }
}

/**
 * Best prior SiteGroup before applying a /Schedule/?SiteGroup= deep link.
 * Prefers localStorage facility, then in-memory atom, then experience cache —
 * never the link code itself.
 */
export function readPriorFacilityForSelfRegLink(linkSiteGroup: string): Facility | null {
  const link = linkSiteGroup.trim().toLowerCase();
  if (!link) return null;

  const candidates: Array<Facility | null> = [
    readPersistedFacility(),
    getDefaultStore().get(selectedFacilityAtom),
    readPersistedExperienceSiteGroup(),
  ];

  for (const candidate of candidates) {
    if (candidate?.id && candidate.id.toLowerCase() !== link) {
      return { id: candidate.id, name: candidate.name || candidate.id };
    }
  }
  return null;
}

/** Write-once prior facility for this interrupt. Cleared only by restore. */
export function stashSelfRegPriorFacilityOnce(
  facility: Facility | null | undefined,
  linkSiteGroup?: string,
): void {
  if (sessionStorage.getItem(SELF_REG_PRIOR_FACILITY_KEY)) return;
  if (!facility?.id) return;
  const link = linkSiteGroup?.trim();
  if (link && facility.id.toLowerCase() === link.toLowerCase()) return;
  sessionStorage.setItem(
    SELF_REG_PRIOR_FACILITY_KEY,
    JSON.stringify({ id: facility.id, name: facility.name || facility.id }),
  );
}

export function loadSelfRegPriorFacility(): Facility | null {
  return parseFacility(sessionStorage.getItem(SELF_REG_PRIOR_FACILITY_KEY));
}

export function loadSelfRegPriorContext(): SelfRegPriorContext | null {
  try {
    const raw = sessionStorage.getItem(SELF_REG_PRIOR_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SelfRegPriorContext;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      facility: loadSelfRegPriorFacility() ?? parsed.facility ?? null,
      authMeta: parsed.authMeta ?? null,
      returnPath: typeof parsed.returnPath === 'string' && parsed.returnPath.startsWith('/')
        ? parsed.returnPath
        : '/home',
    };
  } catch {
    return null;
  }
}

export function clearSelfRegPriorContext(): void {
  sessionStorage.removeItem(SELF_REG_PRIOR_CONTEXT_KEY);
  sessionStorage.removeItem(SELF_REG_PRIOR_FACILITY_KEY);
}

export function releaseSelfRegPriorAuth(): void {
  const facility = loadSelfRegPriorFacility() ?? loadSelfRegPriorContext()?.facility ?? null;
  if (!facility) {
    clearSelfRegPriorContext();
    return;
  }
  stashSelfRegPriorFacilityOnce(facility);
  sessionStorage.setItem(
    SELF_REG_PRIOR_CONTEXT_KEY,
    JSON.stringify({ facility, authMeta: null, returnPath: '/home' } satisfies SelfRegPriorContext),
  );
}

export function setSelfRegSiteGroupEntry(siteGroup: string): void {
  const code = siteGroup.trim();
  if (!code) return;
  sessionStorage.setItem(SELF_REG_SITE_GROUP_ENTRY_KEY, code);
}

export function loadSelfRegSiteGroupEntry(): string | null {
  return sessionStorage.getItem(SELF_REG_SITE_GROUP_ENTRY_KEY)?.trim() || null;
}

export function clearSelfRegSiteGroupEntry(): void {
  sessionStorage.removeItem(SELF_REG_SITE_GROUP_ENTRY_KEY);
}

export function buildSelfRegSchedulePath(siteGroup: string): string {
  return `/Schedule/?SiteGroup=${encodeURIComponent(siteGroup.trim())}`;
}

export function captureSelfRegPriorContext(partial: {
  facility?: Facility | null;
  authMeta?: AuthUserMeta | null;
  returnPath?: string;
  linkSiteGroup?: string;
}): void {
  const existing = loadSelfRegPriorContext();
  const link = partial.linkSiteGroup?.trim();

  const usableNew = (f: Facility | null | undefined): Facility | null => {
    if (!f?.id) return null;
    if (link && f.id.toLowerCase() === link.toLowerCase()) return null;
    return f;
  };

  const facility =
    loadSelfRegPriorFacility()
    ?? existing?.facility
    ?? usableNew(partial.facility)
    ?? (link ? readPriorFacilityForSelfRegLink(link) : null);

  stashSelfRegPriorFacilityOnce(facility, link);

  const nextAuth =
    existing?.authMeta ?? (isFullSession(partial.authMeta ?? null) ? partial.authMeta! : null);
  // Unauthenticated Self-Reg must restore to the landing hub (/home). Defaulting to
  // /appointments (or keeping a stale appointments path after logout) sends cancel through
  // ProtectedRoute → /sign-in, which is wrong for Self-Reg (guest cancel uses /sign-in).
  const nextReturnPath = existing?.returnPath
    ?? (partial.returnPath && partial.returnPath.startsWith('/')
      ? partial.returnPath
      : (nextAuth ? '/appointments' : '/home'));
  const next: SelfRegPriorContext = {
    facility: loadSelfRegPriorFacility() ?? facility,
    authMeta: nextAuth,
    returnPath: nextReturnPath,
  };

  if (!next.facility && !next.authMeta) return;
  sessionStorage.setItem(SELF_REG_PRIOR_CONTEXT_KEY, JSON.stringify(next));
}

export type SelfRegRestoreAppliers = {
  setFacility: (f: Facility | null) => void;
  setAuthMeta: (m: AuthUserMeta | null) => void;
};

/**
 * Restore prior SiteGroup into localStorage + jotai, clear interrupt keys.
 * Returns navigate path. Caller should hard-navigate when a facility was restored
 * so theme/experience cannot stay stuck on the Self-Reg SiteGroup.
 */
export function restoreSelfRegPriorContext(appliers?: SelfRegRestoreAppliers): {
  path: string;
  restoredFacility: Facility | null;
} {
  const ctx = loadSelfRegPriorContext();
  const facility = loadSelfRegPriorFacility() ?? ctx?.facility ?? null;
  const authMeta = ctx?.authMeta ?? null;
  const returnPath = ctx?.returnPath;

  clearSelfRegPriorContext();
  clearSelfRegSiteGroupEntry();
  sessionStorage.removeItem('pp_facility_from_url_param');

  if (!facility && !authMeta) {
    return { path: '/home', restoredFacility: null };
  }

  const store = getDefaultStore();
  if (facility) {
    if (appliers?.setFacility) appliers.setFacility(facility);
    else store.set(selectedFacilityAtom, facility);
    try {
      localStorage.setItem(FACILITY_STORAGE_KEY, JSON.stringify(facility));
    } catch { /* ignore */ }
    try {
      localStorage.removeItem(EXPERIENCE_STORAGE_KEY);
    } catch { /* ignore */ }
    store.set(siteGroupExperienceAtom, null);
  }
  if (authMeta && isFullSession(authMeta)) {
    if (appliers?.setAuthMeta) appliers.setAuthMeta(authMeta);
    else store.set(authUserMetaAtom, authMeta);
  }

  // Only a restored full session may leave Self-Reg for a protected route (e.g. /appointments).
  // Without it, always /home — never a stashed returnPath that would 401 → /sign-in.
  const path = authMeta && isFullSession(authMeta)
    ? (returnPath || '/appointments')
    : '/home';
  return { path, restoredFacility: facility };
}

export function selfRegShouldReuseFullSession(): boolean {
  return isFullSession(loadSelfRegPriorContext()?.authMeta ?? null);
}
